/**
 * The two documents printed after Save (FRD §7):
 *   1. the ROOM SLIP - a working/sorting sheet, explicitly NOT a tax invoice.
 *      The only invoice this module produces is the consolidated client invoice
 *      (FRD §8), so nothing at room level may be called one.
 *   2. the piece tags - one thermal tag per physical piece.
 *
 * Both go out through the same print window the rest of the dashboard uses
 * (components/orders/printDocs.ts) so popup blocking, the hidden-iframe
 * fallback and the thermal page geometry behave identically.
 *
 * THEY ARE TWO PRINT JOBS, NOT ONE. The slip is A5 and the tags are 50×70mm,
 * and a document can only carry one `@page size`. Emitting both together meant
 * the later rule won and one of the two printed at the wrong size - on a
 * TTP-244 loaded with a 50mm roll, an A5 page is a jam. So the slip prints
 * alone, the tags print alone, and neither `@page` block had to change.
 */
import { openPrintWindow } from '../../../components/orders/printDocs'
import { serviceBySlug } from '../serviceCatalogue'
import { formatCurrency, formatDate, formatKg } from '../format'
import { buildRoomTags, isWeightEntry, orderPieceTotal, tagRangeSummary } from './tags'
import type { PieceTag, TaggableLine, TaggableOrder } from './tags'

export interface RoomDocMeta {
  clientName: string
  roomNumber: string
  block: string | null
  /** ISO date the laundry was logged. */
  date: string
}

/** A saved service with its money, which the slip needs and the tags do not. */
export interface RoomSlipOrder extends TaggableOrder {
  /** Server-computed total for the service. Falls back to summing the lines. */
  amount?: number
  items: Array<TaggableLine & { amount?: number }>
}

const esc = (value: unknown) =>
  String(value ?? '').replace(/[&<>"']/g, (character) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] as string,
  )

/**
 * The service's money.
 *
 * The server's `amount` wins when it is there: it is what reaches the invoice,
 * and re-adding the lines here would be a second opinion about the same number.
 */
export const orderAmount = (order: RoomSlipOrder): number =>
  order.amount ?? order.items.reduce((sum, line) => sum + Number(line.amount || 0), 0)

export const roomAmount = (orders: RoomSlipOrder[]): number =>
  orders.reduce((sum, order) => sum + orderAmount(order), 0)

/** Line description: "6.5 kg" for weight services, "Blazer × 2" for item services. */
export const describeOrderLine = (order: RoomSlipOrder): string => {
  if (isWeightEntry(order)) {
    const line = order.items[0]
    return line ? `${formatKg(Number(line.weightKg ?? 0))} kg` : '-'
  }

  return order.items.map((item) => `${item.itemName || 'Item'} × ${item.quantity}`).join(', ')
}

/* --------------------------------------------------------------------- slip */

const SLIP_STYLE = `<style>
  @page { size: A5; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #0F2E2A; margin: 0; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 2px solid #0F2E2A; padding-bottom: 8px; margin-bottom: 12px; }
  .brand { font-size: 16pt; font-weight: 800; letter-spacing: 1px; }
  .kind { font-size: 8pt; letter-spacing: 1px; text-transform: uppercase; color: #556B52; }
  .room { text-align: right; }
  .room-no { font-size: 26pt; font-weight: 800; line-height: 1; }
  .meta { font-size: 9pt; color: #4A5568; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { text-align: left; font-size: 8pt; text-transform: uppercase; letter-spacing: .5px;
    color: #556B52; border-bottom: 1px solid #EAE4D8; padding: 6px 4px; }
  td { font-size: 10pt; padding: 6px 4px; border-bottom: 1px solid #F3EFE6; }
  td.num, th.num { text-align: right; }
  .total { display: flex; justify-content: space-between; font-size: 13pt; font-weight: 800;
    border-top: 2px solid #0F2E2A; margin-top: 10px; padding-top: 8px; }
  .tags { margin-top: 10px; font-size: 9pt; font-weight: 700; }
  .fine { margin-top: 14px; font-size: 8pt; color: #556B52; }
  .page-break { page-break-after: always; break-after: page; }
</style>`

export const buildRoomSlipHtml = (meta: RoomDocMeta, orders: RoomSlipOrder[]): string => {
  const rows = orders
    .map(
      (order) => `
      <tr>
        <td>${esc(serviceBySlug(order.serviceSlug)?.label ?? order.serviceSlug)}</td>
        <td>${esc(describeOrderLine(order))}</td>
        <td class="num">${orderPieceTotal(order)}</td>
        <td class="num">₹${formatCurrency(orderAmount(order))}</td>
      </tr>`,
    )
    .join('')

  return `
    <div class="head">
      <div>
        <div class="brand">RAPIDRY</div>
        <div class="kind">Room slip</div>
        <div class="meta">${esc(meta.clientName)}</div>
        <div class="meta">${esc(formatDate(meta.date))}</div>
      </div>
      <div class="room">
        <div class="kind">Room</div>
        <div class="room-no">${esc(meta.roomNumber)}</div>
        ${meta.block ? `<div class="meta">Block ${esc(meta.block)}</div>` : ''}
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Service</th>
          <th>Weight / Items</th>
          <th class="num">Pieces</th>
          <th class="num">Amount</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="total"><span>Room total</span><span>₹${formatCurrency(roomAmount(orders))}</span></div>
    <div class="tags">Tags: ${esc(tagRangeSummary(orders) || '-')}</div>
    <div class="fine">
      Room slip - not a tax invoice. The payable amount is billed to
      ${esc(meta.clientName)} on the consolidated cycle invoice.
    </div>`
}

/* --------------------------------------------------------------------- tags */

// Same 50mm roll geometry as the order/service labels in printDocs.ts: a fixed
// 50mm box anchored at the page origin, content centred inside it. Do not
// replace this with a centring box across the full page width - see the
// root-cause comment in printDocs.ts for what that does on a TTP-244.
const TAG_STYLE = `<style>
  @page { size: 50mm 70mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; width: 50mm; font-family: Arial, sans-serif;
    color: #000; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .tag { width: 50mm; height: 70mm; padding: 3mm; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 1mm; text-align: center; overflow: hidden;
    page-break-after: always; break-after: page; }
  .tag:last-child { page-break-after: auto; break-after: auto; }
  .brand { font-size: 8pt; font-weight: 800; letter-spacing: .5px; }
  .client { font-size: 9pt; font-weight: 700; max-width: 100%; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; }
  .room { font-size: 24pt; font-weight: 800; line-height: 1; }
  .room-label { font-size: 7pt; letter-spacing: 1px; text-transform: uppercase; }
  .seq { font-size: 18pt; font-weight: 800; line-height: 1.1; }
  .svc { font-size: 9pt; font-weight: 700; }
  .row { font-size: 8pt; }
</style>`

export const buildTagHtml = (meta: RoomDocMeta, tag: PieceTag): string => `
  <div class="tag">
    <div class="brand">RAPIDRY</div>
    <div class="client">${esc(meta.clientName)}</div>
    <div class="room-label">Room</div>
    <div class="room">${esc(meta.roomNumber)}${meta.block ? `<span class="row"> / ${esc(meta.block)}</span>` : ''}</div>
    <div class="svc">${esc(tag.serviceLabel)}</div>
    <div class="seq">${esc(tag.label)}</div>
    <div class="row">${esc(
      tag.itemName ? tag.itemName : tag.weightKg != null ? `${formatKg(tag.weightKg)} kg` : '',
    )}</div>
    <div class="row">${esc(formatDate(meta.date))}</div>
  </div>`

/* ------------------------------------------------------------------- output */

/** One room's slip. A5, on its own - see the note at the top of this file. */
export const openRoomSlip = (meta: RoomDocMeta, orders: RoomSlipOrder[]) => {
  openPrintWindow(
    `Room ${meta.roomNumber} slip. ${meta.clientName}`,
    SLIP_STYLE,
    buildRoomSlipHtml(meta, orders),
  )
}

/** One room's worth of tags, already built, in the order they should print. */
export interface TagJobRoom {
  meta: RoomDocMeta
  tags: PieceTag[]
}

export const jobTagCount = (rooms: TagJobRoom[], copies = 1): number =>
  rooms.reduce((sum, room) => sum + room.tags.length, 0) * Math.max(1, Math.floor(copies) || 1)

/**
 * The tag print run: every selected room's tags, in room order, on the 50mm roll.
 *
 * `copies` repeats each tag consecutively rather than repeating the whole run,
 * so the duplicates for one piece come off the roll together and go onto that
 * piece - reprinting a whole run and then matching them up by hand is the thing
 * an operator with 134 tags cannot do.
 */
export const openTagJob = (rooms: TagJobRoom[], copies = 1, title = 'Rapidry piece tags') => {
  const perTag = Math.max(1, Math.floor(Number(copies)) || 1)
  const body = rooms
    .flatMap((room) =>
      room.tags.flatMap((tag) =>
        Array.from({ length: perTag }, () => buildTagHtml(room.meta, tag)),
      ),
    )
    .join('')

  openPrintWindow(title, TAG_STYLE, body)
}

/** Tags for one room, straight to the printer. */
export const openRoomTags = (meta: RoomDocMeta, orders: TaggableOrder[], copies = 1) =>
  openTagJob([{ meta, tags: buildRoomTags(orders) }], copies, `Room ${meta.roomNumber} tags`)
